import { NgIf } from '@angular/common';
import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LocalUsersApi, LocalsAuthApi } from '@buspro/web/apis';
import { syncAutofilledInputs } from '@buspro/web/core';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzResultComponent } from 'ng-zorro-antd/result';

@Component({
  selector: 'buspro-admin-set-new-password-form',
  templateUrl: './admin-set-new-password-form.component.html',
  standalone: true,
  providers: [NzNotificationService],
  imports: [NgIf, ReactiveFormsModule, NzInputDirective, RouterLink, NzResultComponent],
  styleUrls: ['./admin-set-new-password-form.component.scss'],
})
export class AdminSetNewPasswordFormComponent {
  private fb = inject(FormBuilder);
  private notification = inject(NzNotificationService);
  private route = inject(ActivatedRoute);
  private localsAuthApi = inject(LocalsAuthApi);

  form = this.fb.group({
    password: [null, [Validators.required]],
    password_confirm: [null, [Validators.required]],
  });

  submitted = false;
  /// One-time reset token: a second submit fails with "invalid token" and would
  /// tell a user whose password DID change that something went wrong.
  isSubmitting = signal<boolean>(false);

  private setPasswordFormRef = viewChild<ElementRef<HTMLFormElement>>('setPasswordForm');

  login() {
    if (this.isSubmitting()) return;

    // Adopt anything a password manager filled in — assigning input.value never
    // reaches the control.
    syncAutofilledInputs(this.form, this.setPasswordFormRef()?.nativeElement);

    if (this.form.invalid) {
      // Was a `success` notification asking for an "email and password" on a
      // form that has neither an email field nor anything to celebrate.
      this.form.markAllAsTouched();
      this.notification.error('Password required', 'Please fill in both password fields to continue');
      return;
    }

    if (this.form.get('password')?.value !== this.form.get('password_confirm')?.value) {
      this.notification.error('Passwords does not match', 'Please check your inputs');
      return;
    }

    const { token } = this.route.snapshot.queryParams;
    if (!token) {
      this.notification.error('Invalid token', '');
      return;
    }

    this.isSubmitting.set(true);
    this.localsAuthApi
      .localsAuthControllerResetPassword({
        body: {
          password: this.form.get('password')!.value!,
          confirmPassword: this.form.get('password_confirm')!.value!,
          token,
        },
      })
      .subscribe(
        () => {
          this.isSubmitting.set(false);
          this.submitted = true;
          // this.router.navigateByUrl('/password/login')
          // this.notification.success('Password Changed', 'You can now log in with your new password')
        },
        (error) => {
          this.isSubmitting.set(false);
          console.log(error);
          this.notification.error('Ops! Something went wrong', error.error?.errorMessage);
        },
      );
  }
}
